import { HintAnimation } from '../../portal-hints/hint-motion';
import { reliefFolder } from '../../portal-hints/animations/scene-kit';
import {
  abs,
  bubble,
  C,
  chip,
  glass,
  hline,
  icon,
  okChip,
  orbi,
  panel,
  pill,
  pressed,
  StoryScene,
  text,
  toggle,
} from './story-scene';

/**
 * Vorschau-Szenen der Themen 5–8 (PRD academy-content): je Lektion EINE
 * kurze Szene im Loop — zeigt, worum es geht, taugt als Portalhinweis-Film.
 * Gelernt wird im Klickpfad. Begriffe wie im Portal (assets/i18n/de.json).
 * Panel-Koordinaten ab (20,18); der Zeiger rechnet in Bühnenkoordinaten.
 */

const hidden = (html: string) => html.replace('style="', 'style="opacity:0;');
const box = (x: number, y: number, w: number, h: number, inner: string, opacity = 0.6) =>
  glass(16, opacity, `left:${x}px;top:${y}px;width:${w}px;height:${h}px;`, inner);
/** Gelbe Markierung (Dozent-Bereich: ungelesen / neu). */
const marker = (x: number, y: number, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, 22, 22, 'border-radius:999px;background:#FFC864;display:flex;align-items:center;justify-content:center;')}">${icon('bell', 13, '#8A5A00', 2)}</div>`;
/** Stufenbalken 0–3 wie `app-level-meter`. */
const level = (x: number, y: number, ref: string) =>
  [0, 1, 2].map((i) => `<div style="${abs(x + i * 30, y, 26, 6, 'border-radius:3px;background:rgb(83 109 145 / .16);')}"></div>`).join('') +
  [0, 1, 2].map((i) => `<div data-r="${ref}${i}" style="${abs(x + i * 30, y, 26, 6, `opacity:0;border-radius:3px;background:${C.action};`)}"></div>`).join('');

// ── Thema 5 ─────────────────────────────────────────────────────────────

/** 5.1 — Der Tutor kennt den Kurs, der Twin das Organisatorische der Lehrperson
 *  (FAQ, Wissensordner) und antwortet in ihrem Namen — Badge „Dozent“ darunter. */
const alphaTwin = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      `<div data-r="tutor">${box(30, 22, 240, 92, orbi(14, 14, 40) + text(64, 16, 160, 'Statistik-Tutor', 15, 600) + text(64, 38, 160, 'Alpha Tutor', 12, 500, C.muted) + chip(14, 60, 130, 'Wissen: Kursinhalt'))}</div>` +
        `<div data-r="twin" style="opacity:0;">${box(330, 22, 240, 92, orbi(14, 14, 40) + text(64, 16, 160, 'Prof. Fischer', 15, 600) + text(64, 38, 160, 'Alpha Twin', 12, 500, C.muted) + okChip(14, 60, 170, 'Wissen: FAQ & Ordner'))}</div>` +
        `<svg data-r="link" style="position:absolute;left:0;top:0;opacity:0;" width="600" height="318" fill="none" stroke="${C.accent}" stroke-width="2" stroke-dasharray="5 5"><path d="M270 68 H330"/></svg>` +
        hidden(bubble(310, 138, 260, 'Wie oft darf ich die Prüfung wiederholen?', true, 'ask')) +
        hidden(bubble(30, 198, 330, 'Zweimal. Melde dich jeweils über das Prüfungsamt an — viele Grüße, Prof. Fischer', false, 'answer')) +
        `<div data-r="badge" style="opacity:0;">${chip(30, 260, 92,`${icon('users', 12, C.muted, 2)}&nbsp;Dozent`)}</div>`,
    ),
    tracks: [
      { r: 'tutor', at: [0.6, 1.1], o: [0, 1], y: [10, 0] },
      { r: 'twin', at: [1.8, 2.3], o: [0, 1], y: [10, 0] },
      { r: 'link', at: [3.0, 3.6], o: [0, 1] },
      { r: 'ask', at: [4.2, 4.7], o: [0, 1], y: [8, 0] },
      { r: 'answer', at: [5.6, 6.1], o: [0, 1], y: [8, 0] },
      { r: 'badge', at: [6.9, 7.4], o: [0, 1], y: [6, 0] },
    ],
  });

/** 5.2 — Weitergeleitete Frage beantworten, als geklärt markieren — die KI
 *  meldet über die Glocke einen Wissensvorschlag, der ins FAQ des Twin geht. */
const humanInTheLoop = (): HintAnimation =>
  new StoryScene({
    seconds: 16,
    still: 14.5,
    html: panel(
      // Ohne Navigation: der Inhalt rückt 140px nach links (Panel-Mitte).
      `<div style="position:absolute;left:-140px;top:0;width:600px;height:318px;">` +
      text(186, 22, 200, 'Nachrichten', 15, 600) +
        hidden(pressed(180, 52, 160, 40, 'sel')) +
        orbi(188, 58, 26) + text(222, 62, 100, 'Lea M.', 13, 600) + marker(310, 61, 'mk') +
        `<div style="${abs(188, 104, 26, 26, 'border-radius:999px;background:rgb(83 109 145 / .2);')}"></div>` + text(222, 106, 100, 'Jonas K.', 13, 500, C.muted) +
        `<div style="${abs(352, 50, 2, 250, 'background:linear-gradient(to right, rgb(52 76 117 / .17) 0 1px, rgb(255 255 255 / .78) 1px 2px);')}"></div>` +
        `<div data-r="conv">` +
        `<div data-r="thread" style="opacity:0;">` +
        bubble(366, 58, 210, 'Kann ich die Abgabe auf Freitag verschieben?', false) +
        chip(366, 124, 210, 'An die Lehrperson weitergeleitet') +
        `</div>` +
        hidden(bubble(400, 172, 176, 'Ja, bis Freitag 12 Uhr ist in Ordnung.', true, 'reply')) +
        hidden(pill(400, 226, 176, 'Als geklärt markieren', 'resolve')) +
        `</div>` +
        `<div data-r="bell" style="opacity:0;">${marker(366, 56)}${text(396, 56, 200, 'Neuer Wissensvorschlag', 13, 600)}</div>` +
        `<div data-r="faq" style="opacity:0;">${box(366, 92, 220, 128, text(14, 12, 190, 'FAQ-Vorschlag', 11, 600, C.muted) + `<div style="${abs(14, 32, 192, 40, `font-size:12px;font-weight:600;line-height:1.4;color:${C.text};white-space:normal;`)}">Kann ich die Abgabe verschieben?</div>` + `<div style="${abs(14, 72, 192, 44, `font-size:12px;line-height:1.4;color:${C.text};white-space:normal;`)}">Ja, bis Freitag 12 Uhr ist in Ordnung.</div>`)}</div>` +
        `<div data-r="saved" style="opacity:0;">${okChip(366, 236, 220, 'Im FAQ-Wissen des Twin')}</div>` +
        `</div>`,
      null,
    ),
    tracks: [
      { r: 'sel', at: [1.6, 1.9], o: [0, 1] },
      { r: 'thread', at: [2.0, 2.5], o: [0, 1] },
      { r: 'mk', at: [2.2, 2.6], o: [1, 0] },
      { r: 'reply', at: [6.0, 6.5], o: [0, 1], y: [8, 0] },
      { r: 'resolve', at: [6.9, 7.3], o: [0, 1] },
      { r: 'conv', at: [8.6, 9.0], o: [1, 0] },
      { r: 'bell', at: [9.2, 9.7], o: [0, 1], y: [-6, 0] },
      { r: 'faq', at: [10.6, 11.1], o: [0, 1], y: [8, 0] },
      { r: 'saved', at: [12.4, 12.9], o: [0, 1], y: [6, 0] },
    ],
    cursor: {
      at: [0, 1.2, 2.6, 5.2, 7.4, 8.4, 9.4, 16],
      x: [260, 140, 140, 380, 368, 368, 560, 560],
      y: [280, 90, 90, 300, 260, 260, 310, 310],
      clicks: [1.5, 5.6, 8.0],
    },
  });

/** 5.3 — Übungen mit neuer Einreichung, bewerten. */
const lecturerExercises = (): HintAnimation => {
  const tile = (x: number, name: string, pub: boolean, n: number) =>
    `<div data-r="t${n}">${box(x, 64, 170, 110, `<div style="${abs(14, 14, 8, 8, `border-radius:999px;background:${pub ? C.ok : '#FFC864'};`)}"></div>` + text(28, 8, 130, pub ? 'Veröffentlicht' : 'Entwurf', 11, 600, C.muted) + text(14, 34, 146, name, 14, 600) + text(14, 58, 146, '12 Fragen', 11.5, 500, C.muted))}</div>`;
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(40, 22, 200, 'Übungen', 15, 600) + pill(440, 18, 130, 'Übung erstellen') +
        tile(40, 'Statistik · Kapitel 3', true, 1) + tile(225, 'Varianz & Streuung', true, 2) + tile(410, 'Wahrscheinlichkeit', false, 3) +
        marker(186, 70, 'mk') +
        `<div data-r="detail" style="opacity:0;">${box(40, 190, 520, 100, text(16, 12, 300, 'Einreichung · Lea M.', 13, 600) + text(16, 36, 400, '10 von 12 richtig', 12, 500, C.muted) + pill(16, 58, 110, 'Bewerten', 'grade') + hidden(okChip(16, 62, 110, 'Bewertet', 'graded')), 0.75)}</div>`,
    ),
    tracks: [
      { r: 'mk', at: [0.6, 1.0, 2.6, 2.9], o: [0, 1, 1, 0], s: [0.6, 1, 1, 1] },
      { r: 'detail', at: [2.6, 3.1], o: [0, 1], y: [10, 0] },
      { r: 'grade', at: [6.0, 6.3], o: [1, 0] },
      { r: 'graded', at: [6.1, 6.5], o: [0, 1] },
    ],
    cursor: { at: [0, 1.8, 2.8, 5.2, 12], x: [320, 145, 145, 131, 131], y: [300, 140, 140, 262, 262], clicks: [2.4, 5.8] },
  });
};

/** 5.4 — FAQ anlegen, der Agent antwortet damit. */
const lecturerFaqs = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(40, 22, 200, 'FAQ / Wissen', 15, 600) +
        hline(30, 52, 540) +
        `<div data-r="row">${text(40, 64, 250, 'Wann ist die Klausur?', 13, 600)}${text(300, 64, 260, '14. Februar, 10 Uhr, Raum H1', 13, 500, C.muted)}${hline(30, 92, 540)}</div>` +
        box(150, 120, 300, 170, orbi(12, 10, 24) + text(44, 12, 200, 'Statistik-Tutor', 13, 600) + hline(10, 42, 280), 0.75) +
        hidden(bubble(270, 172, 170, 'Wann schreiben wir?', true, 'q')) +
        hidden(bubble(162, 214, 270, 'Am 14. Februar um 10 Uhr in Raum H1.', false, 'a')) +
        hidden(okChip(162, 256, 120, 'aus den FAQ', 'src')),
    ),
    tracks: [
      { r: 'row', at: [0.6, 1.1], o: [0, 1], x: [20, 0] },
      { r: 'q', at: [2.6, 3.0], o: [0, 1], y: [6, 0] },
      { r: 'a', at: [4.2, 4.6], o: [0, 1], y: [6, 0] },
      { r: 'src', at: [5.2, 5.6], o: [0, 1] },
    ],
  });

// ── Thema 6 ─────────────────────────────────────────────────────────────

/** 6.1 — Echtzeit, Sitzungen, Semantik: drei Fragen, drei Ansichten. */
const analyticsOverview = (): HintAnimation => {
  const bars = [40, 70, 55, 90, 65]
    .map((h, i) => `<div data-r="b${i}" style="${abs(18 + i * 26, 130 - h, 18, h, `border-radius:4px;background:${C.accent};transform-origin:50% 100%;`)}"></div>`)
    .join('');
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      `<div data-r="c1" style="opacity:0;">${box(30, 40, 170, 230, text(14, 12, 140, 'Echtzeit', 13, 600) + text(14, 44, 140, '12', 40, 300) + text(14, 100, 140, 'aktive Gespräche', 12, 500, C.muted))}</div>` +
        `<div data-r="c2" style="opacity:0;">${box(215, 40, 170, 230, text(14, 12, 140, 'Sitzungen', 13, 600) + `<div style="${abs(0, 70, 170, 140)}">${bars}</div>`)}</div>` +
        `<div data-r="c3" style="opacity:0;">${box(400, 40, 170, 230, text(14, 12, 140, 'Semantik', 13, 600) + chip(14, 50, 110, 'Varianz') + chip(14, 82, 130, 'Klausurtermin') + chip(14, 114, 120, 'Normalverteilung'))}</div>`,
    ),
    tracks: [
      { r: 'c1', at: [0.6, 1.1], o: [0, 1], y: [10, 0] },
      { r: 'c2', at: [2.4, 2.9], o: [0, 1], y: [10, 0] },
      ...[0, 1, 2, 3, 4].map((i) => ({ r: `b${i}`, at: [2.9 + i * 0.15, 3.5 + i * 0.15], s: [0, 1] })),
      { r: 'c3', at: [4.6, 5.1], o: [0, 1], y: [10, 0] },
    ],
  });
};

/** 6.2 — Unbeantwortete Fragen finden, Wissen ergänzen. */
const conversationInsights = (): HintAnimation => {
  const q = (y: number, s: string, warn: boolean, n: number) =>
    text(40, y, 330, s, 13, 600) + (warn ? `<div data-r="w${n}">${chip(400, y - 2, 150, 'keine Antwort gefunden')}</div>` : okChip(400, y - 2, 150, 'beantwortet'));
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(40, 22, 200, 'Konversationen', 15, 600) + hline(30, 52, 540) +
        q(66, 'Wie berechne ich die Varianz?', false, 0) + hline(30, 94, 540) +
        hidden(pressed(30, 98, 540, 36, 'sel')) +
        q(106, 'Gibt es eine Formelsammlung?', true, 1) + hline(30, 134, 540) +
        q(146, 'Wann ist die Klausur?', false, 2) +
        `<div data-r="fix" style="opacity:0;">${box(150, 190, 300, 90, text(16, 12, 260, 'Wissensbasis ergänzen', 13, 600) + `<div style="${abs(16, 38, 268, 30, 'display:flex;align-items:center;gap:8px;font-size:12.5px;font-weight:500;')}">${icon('file')}Formelsammlung.pdf</div>`, 0.8)}</div>` +
        hidden(okChip(400, 104, 150, 'jetzt mit Quelle', 'ok')),
    ),
    tracks: [
      { r: 'sel', at: [1.8, 2.1], o: [0, 1] },
      { r: 'fix', at: [3.0, 3.5], o: [0, 1], y: [10, 0] },
      { r: 'w1', at: [6.0, 6.3], o: [1, 0] },
      { r: 'ok', at: [6.1, 6.5], o: [0, 1] },
    ],
    cursor: { at: [0, 1.4, 2.4, 11], x: [300, 260, 260, 260], y: [300, 142, 142, 142], clicks: [1.8] },
  });
};

/** 6.3 — Kompetenzprofil: Stand je Lernziel. */
const learnerProfiles = (): HintAnimation => {
  const row = (y: number, s: string, n: number) => text(190, y, 220, s, 13, 500) + level(420, y + 7, `l${n}`);
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(186, 22, 300, 'Lea M. · Kompetenzprofil', 15, 600) + hline(180, 52, 404) +
        row(70, 'Mittelwert berechnen', 1) + row(110, 'Varianz interpretieren', 2) + row(150, 'Normalverteilung anwenden', 3) +
        hline(180, 190, 404) +
        `<div data-r="mem" style="opacity:0;">${text(190, 206, 200, 'Lerngedächtnis', 13, 600)}${chip(420, 204, 120, 'abgewählt')}</div>`,
      0,
    ),
    tracks: [
      { r: 'l10', at: [0.8, 1.1], o: [0, 1] }, { r: 'l11', at: [1.1, 1.4], o: [0, 1] }, { r: 'l12', at: [1.4, 1.7], o: [0, 1] },
      { r: 'l20', at: [2.2, 2.5], o: [0, 1] }, { r: 'l21', at: [2.5, 2.8], o: [0, 1] },
      { r: 'l30', at: [3.4, 3.7], o: [0, 1] },
      { r: 'mem', at: [5.2, 5.7], o: [0, 1] },
    ],
  });
};

// ── Thema 7 ─────────────────────────────────────────────────────────────

/** 7.3 — Person mit Rolle einladen. */
const usersAndRoles = (): HintAnimation => {
  const user = (y: number, name: string, role: string) =>
    `<div style="${abs(190, y, 28, 28, 'border-radius:999px;background:rgb(83 109 145 / .2);')}"></div>` + text(228, y + 4, 180, name, 13, 600) + chip(470, y + 2, 100, role);
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(186, 22, 200, 'Nutzer & Rechte', 15, 600) + pill(470, 18, 104, 'Einladen', 'inv') + hline(180, 56, 404) +
        user(70, 'Marcel P.', 'Admin') + user(112, 'Anna S.', 'Manager') +
        `<div data-r="new" style="opacity:0;">${user(154, 'tom@hochschule.de', 'Dozent')}${okChip(380, 156, 84, 'eingeladen')}</div>` +
        `<div data-r="dlg" style="opacity:0;">${box(210, 170, 340, 120, text(16, 12, 300, 'Person einladen', 13, 600) + `<div style="${abs(16, 38, 200, 30, `border-radius:999px;background:rgb(255 255 255 / .7);display:flex;align-items:center;padding:0 12px;box-sizing:border-box;font-size:12px;color:${C.text};`)}">tom@hochschule.de</div>` + chip(226, 41, 92, 'Dozent') + pill(200, 78, 120, 'Einladen'), 0.9)}</div>`,
      0,
    ),
    tracks: [
      { r: 'dlg', at: [1.6, 2.0, 5.0, 5.4], o: [0, 1, 1, 0] },
      { r: 'new', at: [5.4, 5.9], o: [0, 1], y: [8, 0] },
    ],
    cursor: { at: [0, 1.0, 1.8, 4.2, 5.2, 11], x: [300, 542, 542, 490, 490, 490], y: [280, 52, 52, 282, 282, 282], clicks: [1.3, 4.6] },
  });
};

/** 7.5 — KI-Hinweis, Lerngedächtnis abwählen, gesperrte Inhalte bleiben gesperrt. */
const privacy = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      box(40, 40, 240, 240, text(16, 14, 200, 'KI-Hinweis', 13, 600) + `<div style="${abs(16, 40, 208, 50, `font-size:12px;line-height:1.45;color:${C.text};white-space:normal;`)}">Du chattest mit einer KI. Antworten können Fehler enthalten.</div>` + pill(16, 100, 110, 'Verstanden'), 0.85) +
        box(320, 40, 250, 110, text(16, 14, 200, 'Datenschutz', 13, 600) + text(16, 52, 160, 'Lerngedächtnis', 12.5, 500) + toggle(190, 50, 'mem'), 0.75) +
        `<div data-r="lock" style="opacity:0;">${box(320, 170, 250, 110, `<div style="${abs(16, 16, 22, 22)}">${icon('lock', 20, C.muted)}</div>` + text(46, 18, 190, 'Klausurlösungen', 13, 600, C.muted) + `<div style="${abs(16, 50, 220, 44, `font-size:11.5px;line-height:1.4;color:${C.text};white-space:normal;`)}">Im LMS gesperrt — der Agent nennt es nicht.</div>`, 0.75)}</div>`,
    ),
    tracks: [
      { r: 'mem', at: [0, 2.4, 2.6], o: [1, 1, 0] },
      { r: 'memKnob', at: [0, 2.4, 2.6], x: [16, 16, 0] },
      { r: 'lock', at: [4.4, 4.9], o: [0, 1], y: [10, 0] },
    ],
    cursor: { at: [0, 1.6, 2.6, 11], x: [300, 549, 549, 549], y: [300, 129, 129, 129], clicks: [2.3] },
  });

// ── Thema 8 ─────────────────────────────────────────────────────────────

/** 8.1 — Vage gegen klare Anweisung. */
const promptCraft = (): HintAnimation =>
  new StoryScene({
    seconds: 12,
    still: 10.5,
    html: panel(
      box(30, 30, 260, 150, text(16, 12, 220, 'Vorher', 12, 600, C.muted) + `<div style="${abs(16, 38, 228, 60, `font-size:13px;line-height:1.45;color:${C.text};white-space:normal;`)}">„Sei hilfreich.“</div>`, 0.6) +
        `<div data-r="after" style="opacity:0;">${box(310, 30, 260, 150, text(16, 12, 220, 'Nachher', 12, 600, C.muted) + chip(16, 40, 110, 'Rolle: Tutor') + chip(132, 40, 112, 'Ziel: verstehen') + chip(16, 72, 110, 'Stil: Beispiele') + chip(132, 72, 112, 'Grenze: keine Lösungen'), 0.75)}</div>` +
        `<div data-r="a1" style="opacity:0;">${bubble(30, 200, 260, 'Die Varianz ist σ² = Σ(x−μ)²/n.', false)}</div>` +
        `<div data-r="a2" style="opacity:0;">${bubble(310, 200, 260, 'Stell dir vor, du misst fünf Mal … wie weit liegt jeder Wert vom Mittel?', false)}${okChip(310, 270, 140, 'trifft das Ziel')}</div>`,
    ),
    tracks: [
      { r: 'a1', at: [1.2, 1.6], o: [0, 1] },
      { r: 'after', at: [3.0, 3.5], o: [0, 1], x: [16, 0] },
      { r: 'a2', at: [5.2, 5.7], o: [0, 1], y: [8, 0] },
    ],
  });

/** 8.2 — Veraltete Fassung entfernen, die Antwort stimmt. */
const knowledgeCuration = (): HintAnimation => {
  const folder = reliefFolder(`<div style="position:absolute;inset:0;">${icon('folderOpen', 20)}</div>`, '');
  return new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      `<div style="${abs(188, 22, 34, 34)}">${folder}</div>` + text(232, 28, 200, 'Prüfungen', 15, 600) + hline(180, 64, 404) +
        `<div data-r="old">${`<div style="${abs(190, 80, 24, 24)}">${icon('file')}</div>`}${text(222, 82, 220, 'Prüfungsordnung_2023.pdf', 13, 600)}${chip(470, 80, 90, 'veraltet')}</div>` +
        `<div style="${abs(190, 122, 24, 24)}">${icon('file')}</div>${text(222, 124, 220, 'Prüfungsordnung_2026.pdf', 13, 600)}${okChip(470, 122, 90, 'aktuell')}` +
        hline(180, 160, 404) +
        hidden(bubble(190, 186, 380, 'Die Bearbeitungszeit beträgt 120 Minuten (Prüfungsordnung 2026).', false, 'a')) +
        hidden(okChip(190, 248, 230, 'Quelle: Prüfungsordnung_2026.pdf', 'src')),
      1,
    ),
    tracks: [
      { r: 'old', at: [2.2, 2.7], o: [1, 0], x: [0, -20] },
      { r: 'a', at: [4.0, 4.4], o: [0, 1], y: [6, 0] },
      { r: 'src', at: [5.0, 5.4], o: [0, 1] },
    ],
    cursor: { at: [0, 1.4, 2.4, 11], x: [300, 575, 575, 575], y: [300, 110, 110, 110], clicks: [2.0] },
  });
};

/** 8.3 — Learning und Guardrail unter „Fähigkeiten“. */
const agentLearnings = (): HintAnimation =>
  new StoryScene({
    seconds: 11,
    still: 9.5,
    html: panel(
      text(40, 22, 200, 'Fähigkeiten', 15, 600) +
        text(40, 58, 90, 'Learnings', 13, 600) + text(140, 58, 100, 'Guardrails', 13, 500, C.muted) +
        `<div style="${abs(40, 80, 70, 3, `border-radius:2px;background:${C.action};`)}"></div>` + hline(30, 86, 540) +
        `<div data-r="l1" style="opacity:0;">${icon('check', 18, C.ok, 2, 'position:absolute;left:40px;top:104px;')}${text(68, 102, 480, 'Bei Prüfungsfragen auf das Skript verweisen', 13, 600)}${hline(30, 132, 540)}</div>` +
        `<div data-r="g1" style="opacity:0;">${`<div style="${abs(40, 148, 20, 20)}">${icon('lock', 18, '#C23A10')}</div>`}${text(68, 148, 480, 'Keine Prüfungslösungen herausgeben', 13, 600)}${chip(68, 176, 110, 'Guardrail')}</div>`,
    ),
    tracks: [
      { r: 'l1', at: [1.0, 1.5], o: [0, 1], x: [16, 0] },
      { r: 'g1', at: [3.6, 4.1], o: [0, 1], x: [16, 0] },
    ],
  });

/** Daumen wie im Chat-Widget (die Szenen-Symbole haben keinen); `down` dreht ihn. */
const thumb = (x: number, y: number, down: boolean, color: string = C.icon) =>
  `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" ` +
  `style="${abs(x, y, 18, 18)}${down ? 'transform:rotate(180deg);' : ''}">` +
  '<path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z"/><path d="M7 10l4-7a2.5 2.5 0 0 1 2.5 2.8L13 10h6a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.8 21H7"/></svg>';

/** 8.4 — Testphase: jede Antwort bewerten → nach Daumen runter filtern →
 *  „Daraus lernen“ → Learning; danach ausrollen. */
const courseRollout = (): HintAnimation =>
  new StoryScene({
    seconds: 16,
    still: 14.5,
    html: panel(
      // Phase 1 — eine Testperson bewertet im Chat.
      `<div data-r="test">` +
        text(30, 22, 300, 'Testphase · Pilotkurs Statistik', 15, 600) + chip(400, 24, 170, 'Jede Antwort bewerten') +
        bubble(330, 62, 240, 'Bis wann muss ich mich zur Prüfung anmelden?', true) +
        bubble(30, 116, 320, 'Bis zum 15. Januar, so steht es im Modulhandbuch.', false) +
        thumb(36, 168, false) +
        `<div data-r="downOn" style="${abs(60, 162, 30, 30, 'opacity:0;border-radius:999px;background:rgb(237 78 27 / .14);')}"></div>` +
        thumb(66, 168, true, '#C23A10') +
        `<div data-r="reason" style="opacity:0;">` +
        box(30, 204, 320, 72, text(14, 10, 290, 'Was war an der Antwort nicht gut?', 12, 700, C.muted) +
          `<div data-r="reasonText" style="${abs(14, 34, 290, 26, `opacity:0;font-size:12.5px;color:${C.text};white-space:nowrap;`)}">Die Frist steht in der Prüfungsordnung.</div>`) +
        `</div>` +
      `</div>` +
      // Phase 2 — Analytics › Konversationen, gefiltert, daraus lernen.
      `<div data-r="conv" style="opacity:0;">` +
        text(30, 22, 300, 'Konversationen', 15, 600) +
        `<div style="${abs(330, 20, 240, 30, 'border-radius:999px;background:rgb(255 255 255 / .55);box-shadow:inset 0 0 0 1px rgb(83 109 145 / .22);')}">` +
          thumb(12, 6, true, '#C23A10') + text(38, 5, 196, 'Daumen runter mit Begründung', 12, 600) + `</div>` +
        box(30, 64, 540, 100,
          text(16, 12, 500, 'Lernende: Bis wann muss ich mich zur Prüfung anmelden?', 12.5, 600) +
          text(16, 38, 500, 'Agent: Bis zum 15. Januar, so steht es im Modulhandbuch.', 12.5, 500) +
          thumb(16, 68, true, '#C23A10') + text(42, 66, 470, 'Begründung: „Die Frist steht in der Prüfungsordnung.“', 12.5, 600, '#C23A10')) +
        hidden(pill(400, 176, 170, 'Daraus lernen', 'learn')) +
        hidden(okChip(30, 222, 300, 'Learning gespeichert — gilt ab sofort', 'saved')) +
        hidden(okChip(30, 262, 360, 'Zufrieden? Testphase beenden und ausrollen', 'rollout')) +
      `</div>`,
      null,
    ),
    tracks: [
      { r: 'downOn', at: [2.0, 2.3], o: [0, 1] },
      { r: 'reason', at: [2.6, 3.1], o: [0, 1], y: [8, 0] },
      { r: 'reasonText', at: [3.4, 3.9], o: [0, 1] },
      { r: 'test', at: [5.6, 6.0], o: [1, 0] },
      { r: 'conv', at: [6.2, 6.7], o: [0, 1] },
      { r: 'learn', at: [7.4, 7.8], o: [0, 1] },
      { r: 'saved', at: [10.0, 10.5], o: [0, 1], y: [6, 0] },
      { r: 'rollout', at: [12.0, 12.5], o: [0, 1], y: [6, 0] },
    ],
    cursor: {
      at: [0, 1.4, 2.6, 4.6, 7.6, 8.6, 9.6, 16],
      x: [300, 95, 95, 300, 505, 505, 590, 590],
      y: [300, 197, 197, 300, 210, 210, 330, 330],
      clicks: [2.0, 9.0],
    },
  });

/** Szenen der Themen 5–8, je ID (Szenen-ID = Lektions-ID). */
export const ADVANCED_SCENES = {
  'alpha-twin': alphaTwin,
  'human-in-the-loop': humanInTheLoop,
  'lecturer-exercises': lecturerExercises,
  'lecturer-faqs': lecturerFaqs,
  'analytics-overview': analyticsOverview,
  'conversation-insights': conversationInsights,
  'learner-profiles': learnerProfiles,
  'users-and-roles': usersAndRoles,
  privacy,
  'prompt-craft': promptCraft,
  'knowledge-curation': knowledgeCuration,
  'agent-learnings': agentLearnings,
  'course-rollout': courseRollout,
} satisfies Record<string, () => HintAnimation>;
